import { type RefObject, useEffect } from 'react';

/**
 * While one finger holds a card (dnd-kit active), a second finger anywhere
 * on screen scrolls the board. The board's overflowX is 'hidden' during drag
 * so we drive scrollLeft directly. Attached to document so it works below
 * the board too.
 */
export function useLeadsBoardSecondFingerScroll(
  isDragging: boolean,
  boardScrollRef: RefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    if (!isDragging) return;

    const container = boardScrollRef.current;
    if (!container) return;

    let secondTouchId: number | null = null;
    let secondTouchStartX = 0;
    let scrollStartLeft = 0;
    let prevTouchX = 0;
    let prevTouchTime = 0;
    let velocityX = 0;
    let inertiaId: number | null = null;

    const cancelInertia = () => {
      if (inertiaId != null) {
        cancelAnimationFrame(inertiaId);
        inertiaId = null;
      }
    };

    const startInertia = (initialVelocity: number) => {
      cancelInertia();
      let v = initialVelocity;
      let lastTime = performance.now();
      const frame = (now: number) => {
        const dt = Math.min(now - lastTime, 32);
        lastTime = now;
        v *= Math.pow(0.93, dt / 16.67);
        const maxScroll = container.scrollWidth - container.clientWidth;
        container.scrollLeft = Math.max(
          0,
          Math.min(maxScroll, container.scrollLeft - v * dt),
        );
        if (Math.abs(v) > 0.05) {
          inertiaId = requestAnimationFrame(frame);
        } else {
          inertiaId = null;
        }
      };
      inertiaId = requestAnimationFrame(frame);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length < 2) return;
      // Don't overwrite an already-active second-finger session (e.g. third finger).
      if (secondTouchId != null) return;
      cancelInertia();
      const t = e.changedTouches.item(0);
      if (!t) return;
      secondTouchId = t.identifier;
      secondTouchStartX = t.clientX;
      scrollStartLeft = container.scrollLeft;
      prevTouchX = t.clientX;
      prevTouchTime = performance.now();
      velocityX = 0;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (secondTouchId == null) return;
      let scrollTouch: Touch | null = null;
      for (let i = 0; i < e.touches.length; i++) {
        const candidate = e.touches.item(i);
        if (candidate?.identifier === secondTouchId) {
          scrollTouch = candidate;
          break;
        }
      }
      if (!scrollTouch) return;

      const now = performance.now();
      const dt = now - prevTouchTime;
      if (dt > 0) {
        const instantV = (scrollTouch.clientX - prevTouchX) / dt;
        velocityX = velocityX * 0.6 + instantV * 0.4;
      }
      prevTouchX = scrollTouch.clientX;
      prevTouchTime = now;

      const dx = scrollTouch.clientX - secondTouchStartX;
      container.scrollLeft = Math.max(
        0,
        Math.min(
          container.scrollWidth - container.clientWidth,
          scrollStartLeft - dx,
        ),
      );
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (secondTouchId == null) return;
      for (let i = 0; i < e.changedTouches.length; i++) {
        const t = e.changedTouches.item(i);
        if (t?.identifier === secondTouchId) {
          secondTouchId = null;
          if (Math.abs(velocityX) > 0.1) startInertia(velocityX);
          return;
        }
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      cancelInertia();
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [boardScrollRef, isDragging]);
}
